import { ESdChangeReason, ISubscriptionDto } from "@kuika/kuika-cl-model";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { PaymentService } from "../../../services/payment-service";
import { SubscriptionService } from "../../../services/subscription-service";
import { UserService } from "../../../services/user-service";
import confJson from "../../../shared/data/conf.json";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../shared/hoc/with-history";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { setManagementLeftPanelCollapsed } from "../../../store/management-layout/actions";
import { ManagementLayoutActionTypes } from "../../../store/management-layout/types";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import BoardLeftPanel from "../board/board-left-panel";
import { CloudLeftPanel } from "./cloud-panel/cloud-left-panel";
import { hasManagementLeftPanel } from "./management-left-panel-routes";
import { ProjectLeftPanel } from "./projects-panel/projects-left-panel";
import { SettingsLeftPanel } from "./settings-panel/settings-left-panel";

const { kuikaDevelopers } = confJson;

declare let window: any;

export interface IManagementLeftPanelState {
  isCreateDomainModalActive: boolean;
  cursor: number;
  result: string[];
  isMenuOpen: boolean;
  currentSubscription: ISubscriptionDto;
  domains?: unknown;
  changePasswordErrorMessage?: string;
}

export interface IManagementLeftPanelProps {}

interface IManagementLeftPanelDispatchProps {
  setManagementLeftPanelCollapsed: (value: boolean) => void;
}

class ManagementLeftPanel extends PureSdComponent<
  IManagementLeftPanelProps & IWithSDProps & IWithContextProp & IWithHistoryProp & IManagementLeftPanelDispatchProps,
  IManagementLeftPanelState
> {
  constructor(props: IManagementLeftPanelProps & IWithSDProps & IWithContextProp & IWithHistoryProp) {
    super(props);
    this.state = {
      isCreateDomainModalActive: false,
      cursor: 0,
      result: [],
      isMenuOpen: false,
      currentSubscription: undefined as unknown as ISubscriptionDto
    };
  }

  componentDidMount = () => {
    try {
      UserService.GetAuthorizedDomains().then(
        (res) => {
          if (res && res.status === 200) {
            this.setState({ domains: res.data });
          }
        },
        (error) => {
          if (error.response.data.message) {
            this.setState({
              changePasswordErrorMessage: error.response.data.message
            });
          }
        }
      );
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }

    const isPlatformOrPreProd =
      window?.location?.hostname === "platform.kuika.com" || window?.location?.hostname === "preprod.kuika.com";
    if (isPlatformOrPreProd) {
      this.loadSubscriptionList();
    }
  };

  async loadSubscriptionList() {
    try {
      const subscriptionList: ISubscriptionDto[] = (await UserService.GetSubscriptionsByDomainId())
        .data as ISubscriptionDto[];
      const subscriptions: ISubscriptionDto[] = (await SubscriptionService.GetSubscriptionPlans())
        .data as ISubscriptionDto[];

      let currentSub = subscriptions[0];
      const currentStripeSubscription = await (await PaymentService.GetCurrentSubscription()).data; // gerçek subscription varmı diye al
      if (currentStripeSubscription && currentStripeSubscription.items && currentStripeSubscription.items.length > 0) {
        currentSub = subscriptions.filter((sub) => sub.stripeId == currentStripeSubscription.items[0].price.id)[0];
      }

      this.setState({
        currentSubscription: currentSub
      });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  }

  openCreateDomainModal = () => {
    this.setState({ isCreateDomainModalActive: true });
  };

  closeCreateDomainModal = () => {
    this.setState({ isCreateDomainModalActive: false });
  };

  selectDomain = async (domainName) => {
    await UserService.ChangeDomain(domainName);
    this.props.navigator.setCurrentDomain(domainName);
  };

  getCurrentDomain = () => {
    return this.props.navigator.getCurrentDomain();
  };

  isUserKuikaDeveloper = (): boolean => {
    return KMainFunctions.isUserKuikaDeveloper(this.props?.context.userInfo?.username);
  };

  render() {
    let pathname = document.location.pathname.substring(1);
    if (pathname.endsWith("/")) {
      pathname = pathname.slice(0, -1);
    }
    const parts = pathname.split(/\//);
    const url = [...parts].pop();
    let breadcrumbHeader = parts[1];
    if (breadcrumbHeader == "projects") breadcrumbHeader = "Apps";
    // Shared with the shell, which reserves the column this fills. `globalconfigurations`
    // is absent from that list: Global Configurations lists every configuration in its own
    // centre table now, so its panel was deleted.
    const isNotDesign: boolean = hasManagementLeftPanel(document.location.pathname);
    const settingsUrl: string | undefined = breadcrumbHeader === "ui-kit" ? "ui-kit" : url;
    return (
      <>
        {isNotDesign && (
          <div className="kuika-left-panel">
            {/* Same collapse affordance as the designer panels: hidden until the panel is
                hovered, sitting on its right edge. Reuses the designer's collapse-only
                handle styles — this container already carries .kuika-left-panel. */}
            <div className="kuika-left-panel__resize-handle kuika-left-panel__resize-handle--collapse-only">
              <div className="kuika-left-panel__edge-collapse">
                <KuikaButton
                  data-testid="managementPanelCollapse"
                  data-onboarding-id="management-left-panel-collapse"
                  type="secondary"
                  size="md"
                  isIcon
                  aria-label="Collapse panel"
                  style={{ boxShadow: "var(--shadow-sm)" }}
                  onClick={() => this.props.setManagementLeftPanelCollapsed(true)}
                >
                  <KuikaSvg type="sidebar-icons" name="layout-sidebar-left-collapse" width={16} height={16} pointer />
                </KuikaButton>
              </div>
            </div>
            <div className="kuika-left-panel__container">
              {(breadcrumbHeader === "settings" || breadcrumbHeader === "ui-kit") && (
                <SettingsLeftPanel url={settingsUrl} />
              )}
              {breadcrumbHeader === "Apps" && <ProjectLeftPanel url={url} />}
              {breadcrumbHeader === "cloud" && <CloudLeftPanel url={url} />}
              {(breadcrumbHeader === "board" || breadcrumbHeader === "issue") && <BoardLeftPanel />}
            </div>
          </div>
        )}
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapDispatchToProps = (dispatch: Dispatch<ManagementLayoutActionTypes>) => ({
  setManagementLeftPanelCollapsed: (value: boolean) => dispatch(setManagementLeftPanelCollapsed(value))
});

const managementLeftPanel = connect(
  null,
  mapDispatchToProps
)(
  withContext<IManagementLeftPanelProps>(
    withHistory<IManagementLeftPanelProps>(withSD<IManagementLeftPanelProps>(ManagementLeftPanel))
  )
);
export { managementLeftPanel as ManagementLeftPanel };
